import { federalStateNames, type CareInsuranceConfig, type Config, type FederalStateConfig, type FederalStateName, type FederalStateProperties } from '@/ts/types/config.types'

export const defaultCareInsuranceValues: CareInsuranceConfig = {
  employer_value: 0.017,
  employee_values: {
    '0': 0.018,
    '0.5': 0.018,
    '1': 0.0155,
    '1.5': 0.013,
    '2': 0.0105,
    '2.5': 0.008
  },
  employee_without_children: 0.006,
  contribution_assessment_limit: 69750
}

const defaultFederalStateProperties: FederalStateProperties = {
  church_tax: 0.09,
  pension_insurance: {
    contribution_assessment_limit: 101400
  },
  unemployment_insurance: {
    contribution_assessment_limit: 101400
  },
  care_insurance: defaultCareInsuranceValues
}

const newFederalStateProperties = {
  pension_insurance: {
    contribution_assessment_limit: 101400
  },
  unemployment_insurance: {
    contribution_assessment_limit: 101400
  }
}

const overrideFederalStatesConfig: Partial<Record<FederalStateName, Partial<FederalStateConfig>>> = {
  'Baden-Württemberg': {
    church_tax: 0.08
  },
  Bayern: {
    church_tax: 0.08
  },
  Brandenburg: newFederalStateProperties,
  'Mecklenburg-Vorpommern': newFederalStateProperties,
  Niedersachsen: newFederalStateProperties,
  Sachsen: {
    ...newFederalStateProperties,
    care_insurance: {
      employer_value: 0.017,
      employee_values: {
        '0': 0.023,
        '0.5': 0.023,
        '1': 0.0205,
        '1.5': 0.018,
        '2': 0.0155,
        '2.5': 0.013
      },
      employee_without_children: 0.006,
      contribution_assessment_limit: 69750
    }
  },
  'Sachsen-Anhalt': newFederalStateProperties,
  Thüringen: newFederalStateProperties
}

export const defaultCalculationConfig: Config = {
  id: 1,
  locale: 'de-DE',
  alias: 'default',
  fields: [
    {
      name: 'purchase_price',
      default: 2500,
      min: 599,
      max: 15000,
      items: []
    },
    {
      name: 'uvp_price',
      default: 2500,
      min: 599,
      max: 20000,
      items: []
    },
    {
      name: 'monthly_salary',
      default: 3000,
      min: 520,
      max: 25000,
      items: []
    },
    {
      name: 'tax_class',
      default: 1,
      min: null,
      max: null,
      items: [
        {
          label: '1',
          value: 1
        },
        {
          label: '2',
          value: 2
        },
        {
          label: '3',
          value: 3
        },
        {
          label: '4',
          value: 4
        },
        {
          label: '5',
          value: 5
        },
        {
          label: '6',
          value: 6
        }
      ]
    },
    {
      name: 'employer_subsidy',
      default: 0.0,
      min: 0,
      max: 550,
      items: []
    },
    {
      name: 'employer_tax_deduction',
      default: true,
      min: null,
      max: null,
      items: [
        {
          label: 'forms.options.yes',
          value: true
        },
        {
          label: 'forms.options.no',
          value: false
        }
      ]
    },
    {
      name: 'assumption_of_insurance_costs',
      default: true,
      min: null,
      max: null,
      items: [
        {
          label: 'forms.options.employer',
          value: true
        },
        {
          label: 'forms.options.employee',
          value: false
        }
      ]
    },
    {
      name: 'federal_state',
      default: 'Nordrhein-Westfalen',
      min: null,
      max: null,
      items: [
        ...federalStateNames
      ]
    },
    {
      name: 'child_allowance',
      default: 'Keine Kinder',
      min: null,
      max: null,
      items: [{ title: 'forms.options.no_children', value: 'Keine Kinder' }, 0.5, 1, 1.5, 2.0, 2.5, 3.0, 3.5, 4.0, 4.5, 5, 5.5, 6]
    },
    {
      name: 'health_insurance',
      default: true,
      min: null,
      max: null,
      items: [
        {
          label: 'forms.options.public_health_insurance',
          value: true
        },
        {
          label: 'forms.options.private_health_insurance',
          value: false
        }
      ]
    },
    {
      name: 'health_insurance_additional_contribution',
      default: 2.9,
      min: 0.01,
      max: 9.99,
      items: []
    },
    {
      name: 'church_tax',
      default: true,
      min: null,
      max: null,
      items: [
        {
          label: 'forms.options.yes',
          value: true
        },
        {
          label: 'forms.options.no',
          value: false
        }
      ]
    },
    {
      name: 'pension_insurance',
      default: true,
      min: null,
      max: null,
      items: [
        {
          label: 'forms.options.yes',
          value: true
        },
        {
          label: 'forms.options.no',
          value: false
        }
      ]
    }
  ],
  federal_states: [],
  factors: {
    leasing_factor: 0.03429,
    duration: 36,
    expected_used_purchase_price_factor: 0.18
  },
  insurance_bonus: [
    {
      price_class: 1500,
      bonus: 21
    },
    {
      price_class: 3000,
      bonus: 23
    },
    {
      price_class: 3000,
      bonus: 23
    },
    {
      price_class: 4000,
      bonus: 26
    },
    {
      price_class: 5000,
      bonus: 29
    },
    {
      price_class: 6000,
      bonus: 32
    },
    {
      price_class: 7000,
      bonus: 35
    },
    {
      price_class: 8000,
      bonus: 38
    },
    {
      price_class: 9000,
      bonus: 41
    },
    {
      price_class: 10000,
      bonus: 44
    },
    {
      price_class: 12500,
      bonus: 50
    },
    {
      price_class: 15000,
      bonus: 55
    }
  ],
  social_insurance: {
    pension_insurance: {
      employer_value: 0.093,
      employee_value: 0.093
    },
    unemployment_insurance: {
      employer_value: 0.013,
      employee_value: 0.013
    },
    health_insurance: {
      employer_value: 0.073,
      employee_value: 0.073,
      contribution_assessment_limit: 69750
    },
    care_insurance: {
      employer_value: 0.017,
      employee_values: {
        '0': 0.018,
        '0.5': 0.018,
        '1': 0.0155,
        '1.5': 0.013,
        '2': 0.0105,
        '2.5': 0.008
      },
      employee_without_children: 0.006,
      contribution_assessment_limit: 69750
    }
  },
  taxes: {
    child_allowance: {
      subsistence_level: 3192,
      care_needs: 1464
    },
    vat: 0.19
  }
}
defaultCalculationConfig.federal_states = [
  federalStateConfig('Baden-Württemberg'),
  federalStateConfig('Bayern'),
  federalStateConfig('Berlin'),
  federalStateConfig('Brandenburg'),
  federalStateConfig('Bremen'),
  federalStateConfig('Hamburg'),
  federalStateConfig('Hessen'),
  federalStateConfig('Mecklenburg-Vorpommern'),
  federalStateConfig('Niedersachsen'),
  federalStateConfig('Nordrhein-Westfalen'),
  federalStateConfig('Rheinland-Pfalz'),
  federalStateConfig('Saarland'),
  federalStateConfig('Sachsen'),
  federalStateConfig('Sachsen-Anhalt'),
  federalStateConfig('Schleswig-Holstein'),
  federalStateConfig('Thüringen')
]

function federalStateConfig(name: FederalStateName): FederalStateConfig {
  const stateConfig = overrideFederalStatesConfig[name]

  return {
    name: name,
    ...defaultFederalStateProperties,
    ...stateConfig
  }
}

